"use client";

import { useState, useEffect } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiPlus,
  HiMinus,
  HiSearch,
  HiChatAlt2,
  HiDocumentText,
  HiCreditCard,
  HiHome,
  HiCog,
  HiLocationMarker,
} from "react-icons/hi";
import { cn } from "@/lib/utils";
import { getFaqData, getFaqCategories } from "@/data/faq";
import { useLanguage } from "@/lib/i18n";

export default function FAQClient() {
  const { language } = useLanguage();
  const faqData = getFaqData(language);
  const categories = getFaqCategories(language);
  
  const [activeCategory, setActiveCategory] = useState(categories[0] || "General");
  const [searchQuery, setSearchQuery] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);

  // Reset activeCategory when language changes
  useEffect(() => {
    const newCategories = getFaqCategories(language);
    setActiveCategory(newCategories[0] || "General");
    setOpenId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Category icons mapping
  const categoryIcons: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    "General": HiDocumentText,
    "Umum": HiDocumentText,
    "Payment": HiCreditCard,
    "Pembayaran": HiCreditCard,
    "Facilities": HiHome,
    "Fasilitas": HiHome,
    "Services": HiCog,
    "Layanan": HiCog,
    "Location": HiLocationMarker,
    "Lokasi": HiLocationMarker,
  };

  const pageContent = language === "id" ? {
    title: "KinKen Help Center",
    subtitle: "Temukan semua jawaban tentang luxury living experience Anda di KinKen Kost Elite Bali. Premium amenities, policies, dan lifestyle.",
    superText: "PREMIUM SUPPORT",
    searchPlaceholder: "Cari jawaban...",
    categoriesTitle: "Kategori",
    searchResults: "Hasil pencarian untuk:",
    noResults: "Tidak ada hasil",
    noResultsDesc: "Coba kata kunci lain atau langsung hubungi concierge kami.",
    clearSearch: "Hapus pencarian & tampilkan semua",
    ctaTitle: "Masih ada pertanyaan?",
    ctaDesc: "Tim concierge kami yang berdedikasi siap membantu Anda dengan pertanyaan spesifik. Layanan premium, tersedia 24/7.",
    chatWhatsApp: "Chat via WhatsApp",
    fastResponse: "(Fast Response)",
    emailConcierge: "Email Concierge",
    avgResponse: "Waktu respons rata-rata:",
    availableIn: "Tersedia dalam",
  } : {
    title: "KinKen Help Center",
    subtitle: "Find all answers about your luxury living experience at KinKen Kost Elite Bali. Premium amenities, policies, and lifestyle.",
    superText: "PREMIUM SUPPORT",
    searchPlaceholder: "Search for answers...",
    categoriesTitle: "Categories",
    searchResults: "Search results for:",
    noResults: "No results found",
    noResultsDesc: "Try another keyword or contact our concierge directly.",
    clearSearch: "Clear search & show all",
    ctaTitle: "Still have questions?",
    ctaDesc: "Our dedicated concierge team is ready to assist you with any specific inquiries. Premium service, available 24/7.",
    chatWhatsApp: "Chat via WhatsApp",
    fastResponse: "(Fast Response)",
    emailConcierge: "Email Concierge",
    avgResponse: "Average response time:",
    availableIn: "Available in",
  };

  const filteredData = faqData.filter((item) => {
    if (searchQuery.length > 0) {
      return (
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return item.category === activeCategory;
  });

  const toggleAccordion = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=2058&auto=format&fit=crop"
        superText={pageContent.superText}
      />

      <section className="py-20 md:py-32 bg-white min-h-[600px]">
        <div className="container mx-auto px-6 md:px-12">
          {/* SEARCH BAR */}
          <div className="max-w-2xl mx-auto mb-16 relative">
            <input
              type="text"
              placeholder={pageContent.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-5 pl-14 bg-[#FAFAFA] border border-gray-200 rounded-lg focus:outline-none focus:border-brand-gold transition-all text-sm shadow-sm font-sans"
            />
            <HiSearch
              className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            {/* SIDEBAR CATEGORIES */}
            {searchQuery.length === 0 && (
              <div className="w-full lg:w-1/4">
                <div className="sticky top-32 space-y-2">
                  <h3 className="font-serif text-xl mb-6 px-4 text-brand-charcoal">
                    {pageContent.categoriesTitle}
                  </h3>
                  {categories.map((cat) => {
                    const IconComponent = categoryIcons[cat] || HiDocumentText;
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setActiveCategory(cat);
                          setOpenId(null);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-4 rounded-lg flex items-center gap-3 transition-all duration-300 text-sm font-sans",
                          activeCategory === cat
                            ? "bg-brand-charcoal text-white shadow-lg"
                            : "text-gray-500 hover:bg-gray-50 hover:text-brand-charcoal",
                        )}
                      >
                        <IconComponent
                          size={18}
                          className={
                            activeCategory === cat
                              ? "text-brand-gold"
                              : "text-gray-400"
                          }
                        />
                        <span className="font-medium">{cat}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CONTENT LIST */}
            <div
              className={cn(
                "w-full",
                searchQuery.length === 0 ? "lg:w-3/4" : "lg:w-2/3 mx-auto",
              )}
            >
              {searchQuery.length > 0 && (
                <p className="mb-6 text-gray-400 text-sm font-sans">
                  {pageContent.searchResults}{" "}
                  <span className="text-brand-charcoal font-bold">
                    "{searchQuery}"
                  </span>
                </p>
              )}

              {/* Category Header */}
              {searchQuery.length === 0 && (
                <div className="mb-8">
                  <h2 className="font-serif text-2xl text-brand-charcoal mb-2">
                    {activeCategory}
                  </h2>
                </div>
              )}

              <div className="space-y-4">
                <AnimatePresence mode="wait">
                  {filteredData.length > 0 ? (
                    filteredData.map((faq) => {
                      const isOpen = openId === faq.id;
                      return (
                        <motion.div
                          key={faq.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className={`border border-gray-100 rounded-xl overflow-hidden transition-all duration-300 ${
                            isOpen
                              ? "bg-white shadow-md border-brand-gold/30"
                              : "bg-[#FAFAFA]"
                          }`}
                        >
                          <button
                            onClick={() => toggleAccordion(faq.id)}
                            className="w-full flex justify-between items-center p-6 text-left group"
                          >
                            <div className="flex-1">
                              <span
                                className={cn(
                                  "font-medium text-lg pr-4 block text-left font-sans",
                                  isOpen ? "text-brand-gold" : "text-brand-charcoal",
                                )}
                              >
                                {faq.question}
                              </span>
                              {faq.isFeatured && (
                                <span className="inline-block mt-2 text-xs px-2 py-1 bg-brand-gold/10 text-brand-gold rounded font-sans font-medium">
                                  POPULAR
                                </span>
                              )}
                            </div>
                            <span
                              className={cn(
                                "flex-shrink-0 transition-transform duration-300 text-gray-400",
                                isOpen && "rotate-180 text-brand-gold",
                              )}
                            >
                              {isOpen ? (
                                <HiMinus size={20} />
                              ) : (
                                <HiPlus size={20} />
                              )}
                            </span>
                          </button>

                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden bg-white"
                              >
                                <div className="p-6 pt-0 text-gray-500 font-light leading-relaxed border-t border-gray-50 mt-2 font-sans">
                                  {faq.answer}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="text-center py-20 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-gray-300">
                        <HiSearch size={32} />
                      </div>
                      <h3 className="font-serif text-xl text-gray-900 mb-2">
                        {pageContent.noResults}
                      </h3>
                      <p className="text-gray-500 font-light text-sm mb-4 font-sans">
                        {pageContent.noResultsDesc}
                      </p>
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-sm text-brand-gold hover:text-brand-gold-dark font-medium font-sans"
                      >
                        {pageContent.clearSearch}
                      </button>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20 bg-gradient-to-r from-brand-charcoal to-[#2A2A2A] text-white text-center px-6">
        <div className="container mx-auto max-w-2xl">
          <div className="w-16 h-16 bg-brand-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <HiChatAlt2 className="w-8 h-8 text-brand-gold" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            {pageContent.ctaTitle}
          </h2>
          <p className="text-gray-300 font-light text-lg mb-8 max-w-lg mx-auto font-sans">
            {pageContent.ctaDesc}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/6281916401702"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-gold-dark text-white px-8 py-4 rounded-lg uppercase text-xs font-bold tracking-[0.2em] transition-all shadow-lg hover:shadow-xl font-sans"
            >
              <span>{pageContent.chatWhatsApp}</span>
              <span className="text-xs font-normal">{pageContent.fastResponse}</span>
            </a>
            <a
              href="mailto:concierge@kinken-kost.com"
              className="inline-flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg uppercase text-xs font-bold tracking-[0.2em] transition-all border border-white/20 font-sans"
            >
              {pageContent.emailConcierge}
            </a>
          </div>

          <p className="text-gray-400 text-sm mt-8 font-sans">
            {pageContent.avgResponse}{" "}
            <span className="text-white font-medium">15 {language === "id" ? "menit" : "minutes"}</span> •
            {pageContent.availableIn}{" "}
            <span className="text-white font-medium">English & Indonesian</span>
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
